/**
 * The `<Application />` component is the entrypoint for the entire application and should only be
 * included within the top-level index file.
 * @module [{components}Application]
 */
import React, {
  useState, useCallback, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Route, Switch, useHistory } from 'react-router-dom';
import { createUseStyles, useTheme } from '@glu/theming';
import dialog from '@glu/dialog';
import util from 'system/util';
import jss from 'jss';

// Page routes.
import { routes } from 'system/routes';

const useStyles = createUseStyles(({
  layoutManager,
  mainContent
}) => ({
  layoutManager: {
    ...layoutManager,
    '@media print': {
      margin: '0 !important'
    }
  },
  mainContent: {
    ...mainContent,
    marginTop: ({ isUnregistered }) => isUnregistered && 50
  }
}));

const isNotAnchorTag = (target) => (target.tagName.toLowerCase() !== 'a' && target.parentNode.tagName.toLowerCase() !== 'a');
const getHrefAttribute = (target) => (target.getAttribute('href') || target.parentNode.getAttribute('href'));
const isNotRoot = (hrefAttribute, root) => (hrefAttribute && hrefAttribute.indexOf(root) === 0);
const hasGlobalsOrFonts = (globals, fonts) => (globals || fonts);
const closeDialog = () => {
  if (dialog.visible()) {
    dialog.close();
  }
};

const Application = ({ getPages, isUnregistered, ...rest }) => () => {
  const classes = useStyles({ isUnregistered, ...rest });
  const [pageRoutes] = useState(getPages());
  const history = useHistory();
  const theme = useTheme();
  const fontStyleSheet = useRef(null);
  /*
   * Need to use this hack due to anchor elements inside marionette views.
   * Otherwise it will do whole page reload
   * TODO: remove this after whole migration to react
  */
  const onClick = useCallback((e) => {
    const { target } = e;

    // there are cases when target is span element and it is child for anchor tag, eg. glu/grid url link
    if (isNotAnchorTag(target)) {
      return true;
    }
    const hrefAttribute = getHrefAttribute(target);

    const { root } = history;

    if (isNotRoot(hrefAttribute, root)) {
      e.preventDefault();
      e.stopPropagation();
      history.push(hrefAttribute.replace(root, ''));
      return false;
    }

    return true;
  }, []);

  const attachFonts = useCallback(() => {
    if (fontStyleSheet.current) {
      fontStyleSheet.current.detach();
    }
    const globals = theme['@global'];
    const fonts = theme['@font-face'];
    if (hasGlobalsOrFonts(globals, fonts)) {
      fontStyleSheet.current = jss.createStyleSheet({
        '@font-face': fonts,
        '@global': globals
      }).attach();
    }
  }, [theme]);

  useEffect(() => {
    const timer = window.setInterval(util.updateWidth(), 50);

    if (isUnregistered) {
      attachFonts();
    }

    return () => window.clearInterval(timer);
  }, []);

  return (
    <>
      {/* TODO: remove connect-container class usage after whole migration to react */}
      <section className={classNames(classes.layoutManager, 'connect-container', 'page-content')}>
        {/*
         Need to use this hack due to anchor elements inside marionette views.
         Otherwise it will do whole page reload
         TODO: remove this after whole migration to react
        */}
        {/* eslint-disable-next-line */}
        <main className={classNames(classes.mainContent, 'main-content')} onClick={onClick} data-qa="main-content">
          <Switch>
            {routes.map(route => {
              if (route.notFoundRoute) {
                return (
                  <Route
                    key="documents-router-notFound"
                    render={() => {
                      closeDialog();
                      document.title = util.generateTitle('notFound');
                      return <pageRoutes.notFound.component />;
                    }}
                  />
                );
              }

              const pageRoute = pageRoutes[route.path];
              return (
                <Route
                  key={`documents-router-${route.path}`}
                  path={route.path}
                  exact={route.exact}
                  render={(props) => {
                    closeDialog();
                    if (!pageRoute) {
                      return <pageRoutes.notFound.component />;
                    }
                    return (
                      <>
                        <pageRoute.component
                          {...props}
                          {...pageRoute.props}
                        />
                      </>
                    );
                  }}
                />
              );
            })}
          </Switch>
        </main>
      </section>
    </>
  );
};

Application.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  getPages: PropTypes.func.isRequired
};

export default Application;
